import { useState } from 'react';
import { supabase } from '../../../lib/supabase';

interface ChangePasswordModalProps {
  profileId: string | null;
  onClose: () => void;
}

type Mode = 'normal' | 'recovery';

export default function ChangePasswordModal({ profileId, onClose }: ChangePasswordModalProps) {
  const [mode, setMode] = useState<Mode>('normal');
  const [currentPassword, setCurrentPassword] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 4) {
      setError('La nueva contraseña debe tener al menos 4 caracteres.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Las contraseñas nuevas no coinciden.');
      return;
    }

    setSaving(true);

    try {
      const { data, error: fetchErr } = await supabase
        .from('dj_profile')
        .select('id, admin_password, recovery_code')
        .maybeSingle();

      if (fetchErr || !data) {
        setError('Error de conexión. Inténtalo de nuevo.');
        setSaving(false);
        return;
      }

      // Verify credential
      if (mode === 'normal') {
        const stored = data.admin_password || 'dj-admin-2025';
        if (currentPassword !== stored) {
          setError('La contraseña actual es incorrecta.');
          setSaving(false);
          return;
        }
      } else {
        const storedCode = data.recovery_code || 'recovery-2025';
        if (recoveryCode !== storedCode) {
          setError('El código de recuperación es incorrecto.');
          setSaving(false);
          return;
        }
      }

      // Get profile id
      const pid = profileId || data.id;
      if (!pid) {
        setError('No se encontró el perfil.');
        setSaving(false);
        return;
      }

      const { error: updateErr } = await supabase
        .from('dj_profile')
        .update({ admin_password: newPassword })
        .eq('id', pid);

      if (updateErr) {
        setError('Error al guardar. Inténtalo de nuevo.');
        setSaving(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => onClose(), 1800);
    } catch (err) {
      console.error('ChangePasswordModal error:', err);
      setError('Error de conexión. Inténtalo de nuevo.');
    }

    setSaving(false);
  };

  const switchMode = (m: Mode) => {
    setMode(m);
    setError('');
    setCurrentPassword('');
    setRecoveryCode('');
    setNewPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
      <div className="bg-gray-900 border border-gray-800 rounded-xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${mode === 'recovery' ? 'ri-key-2-line text-fuchsia-400' : 'ri-lock-password-line text-cyan-400'}`}></i>
            </div>
            {mode === 'recovery' ? 'Recuperar acceso' : 'Cambiar contraseña'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-white transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Mode tabs */}
        <div className="flex border-b border-gray-800">
          <button
            type="button"
            onClick={() => switchMode('normal')}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              mode === 'normal'
                ? 'text-cyan-400 border-b-2 border-cyan-400 -mb-px'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <i className="ri-lock-line mr-1.5"></i>
            Con contraseña actual
          </button>
          <button
            type="button"
            onClick={() => switchMode('recovery')}
            className={`flex-1 py-2.5 text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              mode === 'recovery'
                ? 'text-fuchsia-400 border-b-2 border-fuchsia-400 -mb-px'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            <i className="ri-key-2-line mr-1.5"></i>
            Olvidé mi contraseña
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {success ? (
            <div className="flex flex-col items-center gap-3 py-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-500/20 border border-green-500/50">
                <i className="ri-check-line text-green-400 text-3xl"></i>
              </div>
              <p className="text-green-400 font-medium">¡Contraseña actualizada!</p>
              <p className="text-gray-500 text-xs text-center">Ya puedes iniciar sesión con tu nueva contraseña.</p>
            </div>
          ) : (
            <>
              {mode === 'recovery' && (
                <div className="flex items-start gap-3 bg-fuchsia-500/10 border border-fuchsia-500/30 rounded-lg px-4 py-3">
                  <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-fuchsia-400 text-sm"></i>
                  </div>
                  <p className="text-fuchsia-300 text-xs">
                    Usa el código de recuperación que configuraste en el panel admin.
                  </p>
                </div>
              )}

              {/* Credential input */}
              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">
                  {mode === 'normal' ? 'Contraseña actual' : 'Código de recuperación'}
                </label>
                {mode === 'normal' ? (
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                    placeholder="Tu contraseña actual"
                    required
                  />
                ) : (
                  <input
                    type="text"
                    value={recoveryCode}
                    onChange={(e) => setRecoveryCode(e.target.value)}
                    className="w-full px-4 py-3 bg-black border border-fuchsia-700/50 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-fuchsia-400 transition-all"
                    required
                  />
                )}
              </div>

              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">Nueva contraseña</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                  placeholder="Mínimo 4 caracteres"
                  required
                />
              </div>

              <div>
                <label className="block text-gray-400 text-xs uppercase tracking-wider mb-2">Confirmar nueva contraseña</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-cyan-400 transition-all"
                  placeholder="Repite la nueva contraseña"
                  required
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 bg-red-900/30 border border-red-700/50 rounded-lg px-4 py-3">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-error-warning-line text-red-400 text-sm"></i>
                  </div>
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 font-bold text-sm rounded-lg transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap text-white ${
                    mode === 'recovery'
                      ? 'bg-gradient-to-r from-fuchsia-500 to-cyan-500 hover:shadow-[0_0_20px_rgba(192,38,211,0.4)]'
                      : 'bg-gradient-to-r from-cyan-500 to-fuchsia-500 hover:shadow-[0_0_20px_rgba(6,182,212,0.4)]'
                  }`}
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Guardando...
                    </>
                  ) : (
                    'Guardar'
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
